import React, { useEffect, useRef, useState } from "react";
import QmsModalPortal from "./QmsModalPortal";
import { fetchMyVaultDocumentById } from "./qmsService";
import {
  getVaultDocumentTitle,
  getVaultWorkspaceDocumentDetails,
} from "./qmsVaultUtils";

const MODAL_COPY = {
  checkout: {
    title: "Check out document",
    description: "Provide a reason for checking out this document.",
    confirmLabel: "Check out",
    confirmClass: "btn-primary",
  },
  cancel: {
    title: "Cancel check out",
    description: "Provide a reason for cancelling the check out.",
    confirmLabel: "Cancel check out",
    confirmClass: "btn-danger",
  },
  checkin: {
    title: "Check in document",
    description: "Upload the revised file and provide a check-in comment.",
    confirmLabel: "Check in",
    confirmClass: "btn-primary",
  },
};

const ALLOWED_EXTENSIONS = [
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "pdf",
  "png",
  "jpg",
];

const ACCEPTED_FORMATS_LABEL = ALLOWED_EXTENSIONS.join(", ");

const FILE_ACCEPT_ATTRIBUTE = [
  ...ALLOWED_EXTENSIONS.map((ext) => `.${ext}`),
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/pdf",
  "image/png",
  "image/jpeg",
].join(",");

function getFileExtension(fileName) {
  const name = String(fileName || "").trim();
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) return "";
  return name.slice(lastDot + 1).toLowerCase();
}

function isAllowedCheckInFile(file) {
  if (!file) return false;
  return ALLOWED_EXTENSIONS.includes(getFileExtension(file.name));
}

function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",").pop() : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read the selected file."));
    reader.readAsDataURL(file);
  });
}

function ReadOnlyField({ label, value, fullWidth = false }) {
  return (
    <div
      className={`qms-vault-upload-modal__field${
        fullWidth ? " qms-vault-upload-modal__field--full" : ""
      } qms-vault-checkin-modal__readonly-field`}
    >
      <span className="qms-vault-upload-modal__label">{label}</span>
      <div className="qms-vault-checkin-modal__readonly-value" title={String(value || "—")}>
        {value || "—"}
      </div>
    </div>
  );
}

function QmsVaultCheckInModal({
  open,
  row,
  userData,
  saving = false,
  onClose,
  onConfirm,
}) {
  const fileInputRef = useRef(null);
  const [comments, setComments] = useState("");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [formError, setFormError] = useState("");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [documentDetails, setDocumentDetails] = useState(null);

  useEffect(() => {
    if (!open || !row?.DocumentId) return;

    setComments("");
    setFile(null);
    setDragActive(false);
    setFormError("");
    setDetailsError("");
    setDocumentDetails(null);

    if (!userData?.StaffId) {
      setDocumentDetails(getVaultWorkspaceDocumentDetails(null, row));
      return undefined;
    }

    let cancelled = false;
    setDetailsLoading(true);

    fetchMyVaultDocumentById(userData, row.DocumentId)
      .then((response) => {
        if (cancelled) return;
        setDocumentDetails(getVaultWorkspaceDocumentDetails(response, row));
      })
      .catch((error) => {
        if (cancelled) return;
        setDetailsError(error?.message || "Could not load document details.");
        setDocumentDetails(getVaultWorkspaceDocumentDetails(null, row));
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, row, userData]);

  if (!open || !row) return null;

  const details = documentDetails || getVaultWorkspaceDocumentDetails(null, row);
  const currentExtension = String(row?.DocumentExtension || "").replace(/^\./, "").toUpperCase();

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const applySelectedFile = (nextFile) => {
    if (!nextFile) return;

    if (!isAllowedCheckInFile(nextFile)) {
      setFile(null);
      resetFileInput();
      setFormError(`Unsupported file type. Accepted formats: ${ACCEPTED_FORMATS_LABEL}.`);
      return;
    }

    setFile(nextFile);
    setFormError("");
  };

  const handleFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    if (!nextFile) return;
    applySelectedFile(nextFile);
    if (!isAllowedCheckInFile(nextFile)) {
      event.target.value = "";
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!saving) setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (saving) return;
    applySelectedFile(event.dataTransfer.files?.[0] || null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!file) {
      setFormError("Please choose a revised file to check in.");
      return;
    }
    if (!isAllowedCheckInFile(file)) {
      setFormError(`Unsupported file type. Accepted formats: ${ACCEPTED_FORMATS_LABEL}.`);
      return;
    }

    setFormError("");
    const extension = file.name.includes(".")
      ? file.name.split(".").pop()
      : row.DocumentExtension || "";
    const documentBase64 = await readFileAsBase64(file);
    await onConfirm({
      mode: "checkin",
      comments,
      fileName: file.name,
      documentExtension: extension,
      documentSize: Math.round(file.size / 1024),
      documentBase64,
      documentTitle: getVaultDocumentTitle(row),
      selectedDocumentId: row.DocumentId,
    });
  };

  return (
    <QmsModalPortal>
      <div
        className="qms-vault-upload-modal qms-vault-checkin-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Check in document"
        onClick={() => {
          if (!saving) onClose?.();
        }}
      >
        <div
          className="qms-vault-upload-modal__dialog qms-vault-checkin-modal__dialog"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="qms-vault-upload-modal__content">
            <div className="qms-vault-upload-modal__header">
              <div className="qms-vault-upload-modal__header-main">
                <div className="qms-vault-upload-modal__icon" aria-hidden="true">
                  <i className="fas fa-file-import" />
                </div>
                <div className="qms-vault-upload-modal__title-wrap">
                  <h5 className="qms-vault-upload-modal__title">Check in document</h5>
                  <p className="qms-vault-upload-modal__subtitle">
                    Review document details, upload the revised file, and add an optional comment.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={saving}
              />
            </div>

            <form className="qms-vault-upload-modal__form" onSubmit={handleSubmit}>
              <div className="qms-vault-upload-modal__body">
                <section className="qms-vault-upload-modal__section qms-vault-checkin-modal__summary">
                  <div className="qms-vault-checkin-modal__section-head">
                    <h6 className="qms-vault-checkin-modal__section-title">Document details</h6>
                    {detailsLoading ? (
                      <span className="qms-vault-checkin-modal__loading">
                        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                        Loading…
                      </span>
                    ) : null}
                  </div>

                  {detailsError ? (
                    <p className="qms-vault-checkin-modal__details-warning" role="status">
                      {detailsError} Showing information from the vault list.
                    </p>
                  ) : null}

                  <div className="qms-vault-upload-modal__grid">
                    <ReadOnlyField label="Document ID" value={details.documentId} fullWidth />
                    <ReadOnlyField label="Document title" value={details.displayTitle} fullWidth />
                    <ReadOnlyField label="Document type" value={details.type} />
                    <ReadOnlyField label="Category" value={details.category} />
                    <ReadOnlyField label="Version" value={details.version} />
                    <ReadOnlyField label="Stage" value={details.stage} />
                    <ReadOnlyField label="Checked out by" value={details.checkedOutBy} />
                    <ReadOnlyField label="Checked out on" value={details.checkedOutOn} />
                    <ReadOnlyField
                      label="Checkout reason"
                      value={details.checkoutReason}
                      fullWidth
                    />
                    <ReadOnlyField
                      label="Current file type"
                      value={currentExtension || "—"}
                    />
                  </div>
                </section>

                <section className="qms-vault-upload-modal__section">
                  <div className="qms-vault-upload-modal__grid">
                    <div className="qms-vault-upload-modal__field qms-vault-upload-modal__field--full">
                      <label
                        className="qms-vault-upload-modal__label qms-vault-upload-modal__label--inline"
                        htmlFor="qms-vault-checkin-file"
                      >
                        <span>
                          Revised file <span className="is-required">*</span>
                        </span>
                        <span className="qms-vault-upload-modal__label-hint">
                          Accepted formats: {ACCEPTED_FORMATS_LABEL}
                        </span>
                      </label>
                      <div
                        className={`qms-vault-upload-modal__dropzone${
                          dragActive ? " is-dragover" : ""
                        }${file ? " has-file" : ""}${formError && !file ? " is-invalid" : ""}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <input
                          ref={fileInputRef}
                          id="qms-vault-checkin-file"
                          type="file"
                          className="qms-vault-upload-modal__file-input"
                          accept={FILE_ACCEPT_ATTRIBUTE}
                          onChange={handleFileChange}
                          disabled={saving}
                        />
                        {file ? (
                          <div className="qms-vault-upload-modal__file-card">
                            <div className="qms-vault-upload-modal__file-icon" aria-hidden="true">
                              <i className="fas fa-file-alt" />
                            </div>
                            <div className="qms-vault-upload-modal__file-meta">
                              <strong>{file.name}</strong>
                              <span>{formatFileSize(file.size)}</span>
                            </div>
                            <button
                              type="button"
                              className="qms-vault-upload-modal__file-change"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={saving}
                            >
                              Change file
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="qms-vault-upload-modal__dropzone-trigger"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={saving}
                          >
                            <span className="qms-vault-upload-modal__dropzone-icon" aria-hidden="true">
                              <i className="fas fa-arrow-up" />
                            </span>
                            <strong>Drop your revised file here, or browse</strong>
                            <span>Replace the checked-out version with your updated document</span>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="qms-vault-upload-modal__field qms-vault-upload-modal__field--full">
                      <label className="qms-vault-upload-modal__label" htmlFor="qms-vault-checkin-comments">
                        Check-in comments
                      </label>
                      <textarea
                        id="qms-vault-checkin-comments"
                        className="qms-vault-upload-modal__control qms-vault-checkin-modal__textarea"
                        rows={3}
                        value={comments}
                        onChange={(event) => setComments(event.target.value)}
                        disabled={saving}
                        placeholder="Optional reason or notes about this revision"
                      />
                    </div>
                  </div>
                </section>

                {formError ? (
                  <div className="qms-vault-upload-modal__error" role="alert">
                    <i className="fas fa-exclamation-circle" aria-hidden="true" />
                    <span>{formError}</span>
                  </div>
                ) : null}
              </div>

              <div className="qms-vault-upload-modal__footer">
                <button
                  type="button"
                  className="qms-vault-upload-modal__btn qms-vault-upload-modal__btn--ghost"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="qms-vault-upload-modal__btn qms-vault-upload-modal__btn--primary"
                  disabled={saving || !file}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                      Checking in…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-import" aria-hidden="true" />
                      Check in document
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </QmsModalPortal>
  );
}

function QmsVaultCheckoutModal({
  open,
  mode = "checkout",
  row,
  userData,
  saving = false,
  onClose,
  onConfirm,
}) {
  const [comments, setComments] = useState("");

  useEffect(() => {
    if (!open) return;
    setComments("");
  }, [open, mode, row?.DocumentId]);

  if (!open || !row) return null;

  if (mode === "checkin") {
    return (
      <QmsVaultCheckInModal
        open={open}
        row={row}
        userData={userData}
        saving={saving}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    );
  }

  const copy = MODAL_COPY[mode] || MODAL_COPY.checkout;

  const handleSubmit = async () => {
    await onConfirm({ mode, comments, documentId: row.DocumentId });
  };

  return (
    <>
      <div
        className="modal fade show d-block qms-vault-checkout-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{copy.title}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={saving}
              />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">{copy.description}</p>
              <div className="mb-3">
                <div className="fw-semibold">{getVaultDocumentTitle(row)}</div>
              </div>

              <div className="mb-0">
                <label className="form-label" htmlFor="qms-vault-checkout-comments">
                  {mode === "checkout" ? "Reason" : "Comments"}
                </label>
                <textarea
                  id="qms-vault-checkout-comments"
                  className="form-control"
                  rows={3}
                  value={comments}
                  onChange={(event) => setComments(event.target.value)}
                  disabled={saving}
                  placeholder={
                    mode === "checkout"
                      ? "Why are you checking out this document?"
                      : "Optional reason or notes"
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onClose}
                disabled={saving}
              >
                Close
              </button>
              <button
                type="button"
                className={`btn btn-sm ${copy.confirmClass}`}
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving ? "Saving…" : copy.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
}

export default QmsVaultCheckoutModal;
